/**
 * trustpilot-popup-fix.js
 *
 * Fix for the Trustpilot mini-widget popup getting visually clipped or
 * mispositioned on product cards.
 *
 * Root cause of clipping: .card-product uses `container-type: inline-size`
 * (for the card-product-badges @container query), which implicitly applies
 * layout containment and forces the card into its own stacking context.
 * That traps the popup's z-index inside that one card's paint layer, so
 * the next row of product cards (later in the DOM) paints over it.
 *
 * Fix approach (desktop / >=750px): watch the popup iframe's own `style`
 * attribute (Trustpilot toggles it visible/hidden on it, whether triggered
 * by hover, click, or focus on the widget). The moment it actually becomes
 * visible, move it to document.body with position:absolute, anchored
 * directly below the widget's on-page position. When it's hidden again,
 * move it back to its original spot in the DOM.
 *
 * IMPORTANT: position is recomputed and reapplied on *every* visibility
 * tick while the popup is open (not just the first), because Trustpilot's
 * own script can restyle the iframe again after we've already portaled it
 * (e.g. an opacity/visibility-based close animation instead of a clean
 * display:none, or two style writes in the same tick). If we only compute
 * position once and then ignore later show events, the popup keeps
 * whatever top/left it had from the first time it opened — which goes
 * stale the moment the page reflows in between (lazy-loaded images, the
 * Trustpilot score widget rendering, carousel init, etc). That staleness
 * is exactly what produced the "popup jumps down the page on the second
 * hover" bug. Recomputation is guarded to only write styles when the
 * value actually changes, so it can't loop on its own mutations.
 *
 * MOBILE (<750px): the popup is disabled outright instead of repositioned.
 * Trying to reposition it on mobile turned into a timing race against
 * Trustpilot's own script — their popup init fires a few hundred ms after
 * page load and can re-toggle its own inline style after our JS has
 * already reacted, so no amount of JS-side sequencing reliably wins that
 * race every time (that's the "still shows up sometimes" behavior in the
 * screenshot). CSS doesn't have that problem: a stylesheet rule with
 * `!important` beats an inline `style.display` write regardless of when
 * Trustpilot's script runs, so we inject one `<style>` tag that hard-hides
 * the popup iframe under the mobile breakpoint. We also skip the portal
 * logic entirely on mobile (see `isMobile()` guard in `maybeBind`) so
 * there's no wasted DOM-moving for an iframe that's going to be hidden
 * anyway. Desktop behavior above is completely unchanged.
 */
(function () {
  'use strict';

  // Popup is repositioned at/above this width; disabled entirely below it.
  // Keep this in sync with your theme's actual mobile/desktop breakpoint.
  var MOBILE_BREAKPOINT = 750;

  function isMobile() {
    return window.innerWidth < MOBILE_BREAKPOINT;
  }

  // Injects a CSS rule that force-hides the popup iframe under the mobile
  // breakpoint. This is the actual "disable" — it wins over Trustpilot's
  // own inline style writes no matter when their script initializes, so
  // there's no race condition to lose. Runs once, guarded by element id.
  function injectMobileHideStyle() {
    if (document.getElementById('trustpilot-popup-mobile-hide')) return;
    var style = document.createElement('style');
    style.id = 'trustpilot-popup-mobile-hide';
    style.textContent =
      '@media (max-width: ' + (MOBILE_BREAKPOINT - 1) + 'px) {' +
      '  .trustpilot-widget iframe + iframe {' +
      '    display: none !important;' +
      '    visibility: hidden !important;' +
      '    pointer-events: none !important;' +
      '  }' +
      '}';
    document.head.appendChild(style);
  }

  function initTrustpilotPopupFix() {
    injectMobileHideStyle();
    document.body.addEventListener('mouseover', maybeBind, true);
    document.body.addEventListener('click', maybeBind, true);
    document.body.addEventListener('focusin', maybeBind, true);
  }

  function maybeBind(event) {
    // Below the breakpoint the popup is disabled via CSS (see
    // injectMobileHideStyle) — don't bother portaling/repositioning
    // something that's going to be hidden anyway.
    if (isMobile()) return;

    var widget = event.target.closest('.trustpilot-widget');
    if (!widget) return;

    var iframes = widget.querySelectorAll('iframe');
    if (iframes.length < 2) return; // no popup iframe present

    var popup = iframes[1]; // second iframe is the popup.html widget
    if (popup.dataset.portalBound === 'true') return; // already watching this one

    popup.dataset.portalBound = 'true';
    bindVisibilityObserver(widget, popup);
  }

  function isPopupVisible(popup) {
    return popup.style.display !== 'none';
  }

  function bindVisibilityObserver(widget, popup) {
    var placeholder = null;

    // Recompute where the popup belongs and (re)apply it. Safe to call
    // repeatedly: it only touches style properties whose value actually
    // needs to change, so calling it every time the popup's style mutates
    // (including because of our own writes) is a no-op once it's correct.
    function applyPosition() {
      // visualViewport reflects the real visible area on mobile (excludes
      // area hidden behind the address bar / keyboard); fall back to the
      // window dimensions on browsers without it.
      var vv = window.visualViewport;
      var viewportWidth = vv ? vv.width : window.innerWidth;
      var viewportHeight = vv ? vv.height : window.innerHeight;
      var vvOffsetLeft = vv ? vv.offsetLeft : 0;
      var vvOffsetTop = vv ? vv.offsetTop : 0;

      var rect = widget.getBoundingClientRect();

      var margin = 8;
      var gap = 4; // small breathing room between the widget and the popup
      var desiredWidth = 280; // Trustpilot's natural popup content width
      var maxWidth = viewportWidth - margin * 2;
      var width = Math.min(desiredWidth, maxWidth);

      var minLeft = margin + vvOffsetLeft + window.scrollX;
      var maxLeft = viewportWidth - width - margin + vvOffsetLeft + window.scrollX;
      var left = Math.min(Math.max(rect.left + window.scrollX, minLeft), maxLeft);

      // Force the width now so the height we measure next reflects how the
      // popup will actually render at this width, then figure out which
      // side has room to fit it.
      if (popup.style.position !== 'absolute') popup.style.position = 'absolute';
      var newWidth = width + 'px';
      if (popup.style.width !== newWidth) popup.style.width = newWidth;

      var popupHeight = popup.offsetHeight;
      var spaceBelow = (viewportHeight + vvOffsetTop) - rect.bottom;
      var spaceAbove = rect.top - vvOffsetTop;

      var top;
      if (popupHeight <= spaceBelow || spaceBelow >= spaceAbove) {
        // Anchor below the widget (default, matches desktop behavior).
        top = rect.bottom + window.scrollY + gap;
      } else {
        // Not enough room below — flip to anchor above the widget instead.
        top = rect.top + window.scrollY - popupHeight - gap;
        // Never push it above the top of the page.
        top = Math.max(top, margin + vvOffsetTop + window.scrollY);
      }

      var newTop = top + 'px';
      var newLeft = left + 'px';

      if (popup.style.left !== newLeft) popup.style.left = newLeft;
      if (popup.style.top !== newTop) popup.style.top = newTop;
      if (popup.style.zIndex !== '9999') popup.style.zIndex = '9999';
    }

    var repositionScheduled = false;
    function scheduleReposition() {
      if (popup.dataset.portalActive !== 'true' || repositionScheduled) return;
      repositionScheduled = true;
      window.requestAnimationFrame(function () {
        repositionScheduled = false;
        if (popup.dataset.portalActive === 'true') applyPosition();
      });
    }

    function showPopup() {
      if (popup.dataset.portalActive !== 'true') {
        popup.dataset.portalActive = 'true';

        // Mark the original spot so we can put it back later
        placeholder = document.createElement('span');
        placeholder.style.display = 'none';
        placeholder.setAttribute('data-trustpilot-popup-placeholder', '');
        popup.parentNode.insertBefore(placeholder, popup);

        document.body.appendChild(popup);

        // Mobile browsers resize the visual viewport (address bar
        // show/hide, keyboard) without touching the popup's style, so the
        // MutationObserver alone won't catch it — listen while open.
        // passive: true so these can never block scroll compositing.
        window.addEventListener('resize', scheduleReposition, { passive: true });
        window.addEventListener('scroll', scheduleReposition, { passive: true, capture: true });
        if (window.visualViewport) {
          window.visualViewport.addEventListener('resize', scheduleReposition, { passive: true });
          window.visualViewport.addEventListener('scroll', scheduleReposition, { passive: true });
        }
      }

      // Always reassert the correct position, even on repeat show events
      // for a popup that's already portaled — see comment above.
      applyPosition();
    }

    function hidePopup() {
      if (popup.dataset.portalActive !== 'true') return;

      window.removeEventListener('resize', scheduleReposition);
      window.removeEventListener('scroll', scheduleReposition, { capture: true });
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', scheduleReposition);
        window.visualViewport.removeEventListener('scroll', scheduleReposition);
      }

      popup.style.position = '';
      popup.style.left = '';
      popup.style.top = '';
      popup.style.width = '';
      popup.style.zIndex = '';

      if (placeholder && placeholder.parentNode) {
        placeholder.parentNode.insertBefore(popup, placeholder);
        placeholder.remove();
      }
      placeholder = null;
      delete popup.dataset.portalActive;
    }

    var observer = new MutationObserver(function () {
      if (isPopupVisible(popup)) {
        showPopup();
      } else {
        hidePopup();
      }
    });

    observer.observe(popup, { attributes: true, attributeFilter: ['style'] });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTrustpilotPopupFix);
  } else {
    initTrustpilotPopupFix();
  }
})();